/**
 * Home testimonials: real Google reviews only.
 *
 * Source: functions/data/reviews-harvest.json (public Google Maps reviews of
 * the practice, harvested 2026-08-26, newest 30 five-star reviews with text).
 * `sourceIndex` is the 0-based index in that file's `reviews` array and
 * `authorUri` the reviewer's Google profile, so every quote can be checked.
 * Picked from restyle-research/research_notes/testimonial-shortlist.md (top 6).
 *
 * Rules: quote text is verbatim from the review, trimmed with "..." only,
 * never reworded (curly apostrophes and the reviewer's own punctuation kept).
 * First name + last initial. One review per family. Each names a concrete
 * change. Never use SITE.testimonials here (synthetic, see docs/google-reviews.md).
 */
// ASK Zach: confirm families + consent before go-live
export interface HomeTestimonial {
  /** First name + last initial, as shown on the page. */
  name: string;
  quote: string;
  /** The concrete change the family names (for review only, not rendered). */
  change: string;
  sourceIndex: number;
  authorUri: string;
  /** Needs an explicit consent check before going live (health detail about a child or family member). */
  consentFlag?: string;
}

/** The one large pull quote. */
export const FEATURED_TESTIMONIAL: HomeTestimonial = {
  name: 'Lee B.',
  quote:
    'My back pain was so bad, it was taking my breath away. ... After that first treatment, I was able to move again and I have never had pain that severe again.',
  change: 'Severe back pain; moving again after the first visit, never that severe since',
  sourceIndex: 13,
  authorUri: 'https://www.google.com/maps/contrib/101228870769515799562/reviews?hl=en',
};

/** The quieter set under it (order = reading order). */
export const TESTIMONIALS: HomeTestimonial[] = [
  {
    name: 'Kaylee B.',
    quote:
      '...my baby wasn’t going to the bathroom and after one adjustment, she was able to go again within the same day! We’d also attribute her ability to sleep through the night, pretty early on, due to her chiropractic adjustments.',
    change: 'Baby constipation eased the same day; early sleeping through the night',
    sourceIndex: 24,
    authorUri: 'https://www.google.com/maps/contrib/101695128670789261084/reviews?hl=en',
    consentFlag: 'Health detail about her baby',
  },
  {
    name: 'Carla M.',
    quote:
      'I needed help with vertigo asap and I called and they got me right in without ever seeing me before. ... I was only evaluated by Dr. Zach but he was awesome in explaining everything clearly and did wonders for my vertigo.',
    change: 'Vertigo; seen the same day as a new patient',
    sourceIndex: 3,
    authorUri: 'https://www.google.com/maps/contrib/108085715921186680090/reviews?hl=en',
  },
  {
    name: 'Christina K.',
    quote: '...Had a wonderful pregnancy and delivery thanks to their care.',
    change: 'Pregnancy and delivery',
    sourceIndex: 8,
    authorUri: 'https://www.google.com/maps/contrib/117436956653581700781/reviews?hl=en',
  },
  {
    name: 'Mike R.',
    quote: '...Today was about the kids! 2/3 with hip issues! All good now thanks to Doc and friends!',
    change: "Two of three kids' hip issues",
    sourceIndex: 6,
    authorUri: 'https://www.google.com/maps/contrib/101292179321236773620/reviews?hl=en',
    consentFlag: "Mentions his children's hip issues",
  },
  {
    name: 'Melissa B.',
    quote:
      '...My daughter was in a car accident this week. ... Dr. John took a lot of time to help her body heal ... Dr. Saylor also took a lot of time continuing the process of helping her body heal. Today, she feels very little residual pain from the accident.',
    change: "Daughter's car accident; very little residual pain within days",
    sourceIndex: 9,
    authorUri: 'https://www.google.com/maps/contrib/109076740856414144292/reviews?hl=en',
    consentFlag: "Names her daughter's car accident",
  },
];
